import React, { useEffect, useMemo } from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useTablePagination, useAppMeta } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyListBSLReportQuery } from "store/services/BranchShipmentLocationApi";
import BSLReportFilter from "./BranchShipmentLocationComponents/BSLReportFilter";

const BSLReportListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { params } = useCustomParams();

  const keyword = searchParams.get("search") || "";
  const fromDate = params.fromDate || "";
  const toDate = params.toDate || "";

  const [fetchBSLReport, { data: bslRes, isLoading, isFetching, error }] =
    useLazyListBSLReportQuery();

  useEffect(() => {
    fetchBSLReport({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
    });
  }, [currentPage, perPage, keyword, fromDate, toDate, fetchBSLReport]);

  useAppMeta("BSL Report");

  const handleViewShipments = (row) => {
    // Navigate to BSL view screen, pre-filtered by this branch
    navigate(
      `/app/crm/reports/branch-shipment-location-view?branch=${row.branch}`,
    );
  };

  const columns = useMemo(
    () => [
      {
        name: "Branch Name",
        selector: (row) => row.branchName || "N/A",
        cell: (row) => (
          <HighlightedText text={row.branchName || "N/A"} highlight={keyword} />
        ),
      },
      {
        name: "Branch Code",
        selector: (row) => row.branchCode || "N/A",
      },
      {
        name: "Transit Shipments",
        selector: (row) => Number(row.transitCount || 0),
        cell: (row) => (
          <Chip
            label={Number(row.transitCount || 0)}
            size="small"
            sx={{
              backgroundColor: "#FFF3CD",
              color: "#856404",
              fontWeight: "bold",
              height: "22px",
              borderRadius: "8px",
              "& .MuiChip-label": { fontSize: "0.75rem", padding: "0 8px" },
            }}
          />
        ),
      },
      {
        name: "Destination Shipments",
        selector: (row) => Number(row.destinationCount || 0),
        cell: (row) => (
          <Chip
            label={Number(row.destinationCount || 0)}
            size="small"
            sx={{
              backgroundColor: "#D1ECF1",
              color: "#0C5460",
              fontWeight: "bold",
              height: "22px",
              borderRadius: "8px",
              "& .MuiChip-label": { fontSize: "0.75rem", padding: "0 8px" },
            }}
          />
        ),
      },
      {
        name: "Source Shipments",
        selector: (row) => Number(row.sourceCount || 0),
        cell: (row) => (
          <Chip
            label={Number(row.sourceCount || 0)}
            size="small"
            sx={{
              backgroundColor: "#EDE7F6",
              color: "#4527A0",
              fontWeight: "bold",
              height: "22px",
              borderRadius: "8px",
              "& .MuiChip-label": { fontSize: "0.75rem", padding: "0 8px" },
            }}
          />
        ),
      },
      {
        name: "Total Shipments",
        selector: (row) => Number(row.totalCount || 0),
        cell: (row) => (
          <Chip
            label={Number(row.totalCount || 0)}
            size="small"
            sx={{
              backgroundColor: "#D4EDDA",
              color: "#155724",
              fontWeight: "bold",
              height: "22px",
              borderRadius: "8px",
              "& .MuiChip-label": { fontSize: "0.75rem", padding: "0 8px" },
            }}
          />
        ),
      },
      {
        name: "View",
        cell: (row) => (
          <Tooltip title="View Shipments in this Branch">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewShipments(row)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [keyword],
  );

  return (
    <BreadcrumbLayout
      breadcrumbs={[
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "BSL Report" },
      ]}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BSLReportFilter disabled={isLoading || isFetching} />

        <Box sx={{ px: 2, py: 1 }}>
          <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Transit
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#856404" }}>
                {bslRes?.totals?.totalTransit || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Destination
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#0C5460" }}>
                {bslRes?.totals?.totalDestination || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Source
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#4527A0" }}>
                {bslRes?.totals?.totalSource || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Grand Total
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {bslRes?.totals?.grandTotal || 0}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <AppTable
          columns={columns}
          data={bslRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={bslRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default BSLReportListScreen;